import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, map, Observable, of, shareReplay, switchMap, take, timeout } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthRedirectService, AuthService, AuthStorageService, OccEndpointsService } from '@spartacus/core';
import { SsoLogin } from '../core/services/sso-login.service';
import { environment } from '../../environments/environment';
import {BnNgIdleService} from 'bn-ng-idle';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardRequestOptions, TableDataResponse, ReviewOrderResponse, AccountListResponse, AccountUpdateResponse, AccountType, ConsignmentReviewRow, CartEntryUpdatePayload, RejectDocumentPayload, ConsignmentReviewPayload, SerialValidationEntry, NotificationMessage, AgentSiteConfigurationResponse, RejectModalConfig } from './pages.models';

@Injectable({
  providedIn: 'root',
})

export class PagesService {
  private static readonly PURCHASE_ORDER_TITLE_KEY = 'purchaseOrderTitle';
  private static readonly REVIEW_FINAL_ACTION_KEY = 'reviewFinalActionCartIds';
  private static readonly REVIEW_EXIT_REQUEST_TIMEOUT_MS = 3000;
  private purchaseOrderTitleSubject = new BehaviorSubject<string | null>(null);

  private messageSubject = new BehaviorSubject<NotificationMessage | null>(null);
  message$ = this.messageSubject.asObservable();

  private navigationNodesSubject = new BehaviorSubject<any>(null);
  navigationNodes$ = this.navigationNodesSubject.asObservable();

  public reviewOrderDataSubject = new BehaviorSubject<ReviewOrderResponse | null>(null);
  readonly reviewOrderData$ = this.reviewOrderDataSubject.asObservable();

  private isSessionExpiredSubject = new BehaviorSubject<boolean>(false);
  isSessionExpired$ = this.isSessionExpiredSubject.asObservable();

  private currentIdleTimeoutSubject = new BehaviorSubject<number>(environment.IdleUserLogoutTime);
  currentIdleTimeout$ = this.currentIdleTimeoutSubject.asObservable();

  timeZone: string;
  userId: string | null = null;
  isLoggedOut: boolean = false;
  idleLogoutInitialized = false;
  private readonly destroyRef = inject(DestroyRef);
  private homeComponentData$!: Observable<any>;
  private activeReviewCartId: string | null = null;
  private reviewCartExitHandled = false;
  private readonly persistedReviewExitCartIds = new Set<string>();
  // In-flight review-exit sequence (header/entry saves + updateCartStatus) that dashboard reads must wait for.
  private reviewExit$: Observable<unknown> | null = null;
  // Cart whose final business action (place order / reject / consignment review) owns the backend status.
  // Kept in sessionStorage so a re-created review page or a reload cannot lose the marker.
  private readonly reviewFinalActionCartIds = new Set<string>();
  private currentUrl: string | null = null;
  private previousUrl: string | null = null;

  private buildHomeComponentDataStream(): Observable<any> {
    const homeComponentUrl = this.occEndpointsService.buildUrl('components/homepage');
    return this.http.get<any>(homeComponentUrl).pipe(
      // Cache result and share among all subscribers.
      shareReplay(1),
      catchError((error) => {
        console.error('Failed to fetch home component data:', error);
        return of({ response: [] });
      })
    );
  }
  
  constructor(
    private http: HttpClient, 
    private occEndpointsService: OccEndpointsService,
    private bnIdle: BnNgIdleService,
    private auth: AuthService,
    private authRedirectService: AuthRedirectService,
    private authStorageService: AuthStorageService,
    private router: Router,
    private ssoLogin: SsoLogin
  ) {
    // Initialize cached observable - single API call for home component data.
    this.homeComponentData$ = this.buildHomeComponentDataStream();
    
    // Initialize idle logout once when service is created
    this.initializeIdleLogout();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      });
  }

  getPreviousUrl(): string | null {
    return this.previousUrl;
  }

  setMessage(message: NotificationMessage): void {
    this.messageSubject.next(message);
  }

  clear(): void {
    this.messageSubject.next(null);
  }

  registerActiveReviewCart(cartId: string): void {
    this.activeReviewCartId = cartId;
    this.reviewCartExitHandled = false;
    this.reviewExit$ = null;
    this.persistedReviewExitCartIds.delete(cartId);
  }

  /**
   * Flags a cart whose final business action is executing or completed. While flagged, no exit
   * handler may send /updateCartStatus, so the status returned by that action is never overwritten.
   */
  setReviewFinalAction(cartId: string, inProgress: boolean): void {
    if (!cartId) {
      return;
    }

    this.restoreReviewFinalActions();
    if (inProgress) {
      this.reviewFinalActionCartIds.add(cartId);
    } else {
      this.reviewFinalActionCartIds.delete(cartId);
    }

    try {
      sessionStorage.setItem(PagesService.REVIEW_FINAL_ACTION_KEY, JSON.stringify([...this.reviewFinalActionCartIds]));
    } catch {
      // Storage is optional; the in-memory set still guards this tab.
    }
  }

  isReviewFinalActionCart(cartId: string): boolean {
    if (!cartId) {
      return false;
    }

    this.restoreReviewFinalActions();
    return this.reviewFinalActionCartIds.has(cartId);
  }

  private restoreReviewFinalActions(): void {
    try {
      const stored = JSON.parse(sessionStorage.getItem(PagesService.REVIEW_FINAL_ACTION_KEY) ?? '[]');
      if (Array.isArray(stored)) {
        stored.forEach((cartId) => typeof cartId === 'string' && this.reviewFinalActionCartIds.add(cartId));
      }
    } catch {
      // Ignore unreadable storage and rely on the in-memory set.
    }
  }

  /**
   * Registers the review page's exit sequence so dashboard requests are queued behind it.
   * The stream must be shared/replayed by the caller; it is only subscribed to, never re-run.
   */
  registerReviewExit(exit$: Observable<unknown>): void {
    this.reviewExit$ = exit$;
  }

  /** Emits once the pending review-exit sequence has finished, or immediately when there is none. */
  private afterReviewExit(): Observable<unknown> {
    if (!this.reviewExit$) {
      return of(null);
    }

    // A failed exit must not block the dashboard from loading.
    return this.reviewExit$.pipe(catchError(() => of(null)));
  }

  clearActiveReviewCart(cartId?: string): void {
    if (!cartId || this.activeReviewCartId === cartId) {
      this.activeReviewCartId = null;
      this.reviewCartExitHandled = false;
    }
  }

  markActiveReviewCartLeft(useBeacon = false): void {
    if (!this.activeReviewCartId || this.reviewCartExitHandled) {
      return;
    }

    const cartId = this.activeReviewCartId;
    this.reviewCartExitHandled = true;
    this.activeReviewCartId = null;
    this.persistedReviewExitCartIds.add(cartId);

    if (this.isReviewFinalActionCart(cartId)) {
      return;
    }

    if (useBeacon) {
      this.sendUpdateCartStatusBeacon(cartId);
      return;
    }

    this.updateCartStatus(cartId, false).subscribe();
  }

  /**
   * Marks the cart as no longer in review. Callers must have persisted header and
   * line-item changes first so the backend recalculates status from the saved data.
   */
  persistReviewExit(cartId: string): Observable<boolean> {
    if (!cartId || this.persistedReviewExitCartIds.has(cartId) || this.isReviewFinalActionCart(cartId)) {
      return of(true);
    }

    this.persistedReviewExitCartIds.add(cartId);
    this.clearActiveReviewCart(cartId);

    return this.updateCartStatus(cartId, false).pipe(
      timeout(PagesService.REVIEW_EXIT_REQUEST_TIMEOUT_MS),
      map(() => true),
      catchError(() => of(true))
    );
  }

  
  updateNavigationNodes(value: any) { 
    this.navigationNodesSubject.next(value);  
  }

  setPurchaseOrderTitle(title: string | null): void {
    const trimmed = title?.trim();
    const normalizedTitle = trimmed ? trimmed : null;
    if (normalizedTitle) {
      sessionStorage.setItem(PagesService.PURCHASE_ORDER_TITLE_KEY, normalizedTitle);
    }else{
      sessionStorage.removeItem(PagesService.PURCHASE_ORDER_TITLE_KEY);
    }
    this.purchaseOrderTitleSubject.next(normalizedTitle);
  }

  getPurchaseOrderTitle(): string | null {
    if (this.purchaseOrderTitleSubject.value) {
      return this.purchaseOrderTitleSubject.value;
    }

    const storedTitle = sessionStorage.getItem(PagesService.PURCHASE_ORDER_TITLE_KEY);
    if (storedTitle?.trim()) {
      this.purchaseOrderTitleSubject.next(storedTitle.trim());
      return storedTitle.trim();
    }

    return null;
  }

  private getDashboardData(options: DashboardRequestOptions): Observable<TableDataResponse> {

    const orderType = options.orderType?.trim() || 'ALL';
    const payload = {
      homepage: options.homepage,
      orderType: orderType
    };
    const url = this.occEndpointsService.buildUrl('agent/dashboard');
    return this.http.post<TableDataResponse>(url, payload).pipe(
      catchError((error) => {
        console.error('Failed to fetch dashboard data:', error);
        return of({ response: [] });
      })
    );
  }

  /**
   * Loads dashboard rows only after any pending review-exit sequence has completed,
   * so the backend has the latest header/line-item data when it calculates status.
   */
  private getDashboardDataAfterReviewExit(homepage: boolean): Observable<TableDataResponse> {
    return this.afterReviewExit().pipe(
      switchMap(() => this.getDashboardData({ homepage, orderType: 'ALL' }))
    );
  }

  getTableData(): Observable<TableDataResponse> {
    return this.getDashboardDataAfterReviewExit(true);
  }

  getReviewOrderData(cartId: string): Observable<ReviewOrderResponse> {
    const url = this.occEndpointsService.buildUrl(`/agent/${cartId}/review`);
    return this.http.get<ReviewOrderResponse>(url).pipe(
      catchError(error => {
        console.error('Failed to fetch review order data:', error);
        throw error;
      })
    );
  }

  /** Downloads an attachment as a blob; pdf.js range requests would otherwise trigger a blocked CORS preflight. */
  getAttachmentBlob(downloadUrl: string): Observable<Blob> {
    const baseUrl = environment.baseUrl.replace(/\/$/, '');
    const baseOrigin = new URL(baseUrl, window.location.origin).origin;
    const url = new URL(downloadUrl, `${baseUrl}/`);

    if (url.origin !== baseOrigin) {
      throw new Error('Attachment URL must use the configured backend origin.');
    }

    return this.http.get(url.toString(), { responseType: 'blob' });
  }

  getCurrentReviewOrderData(): ReviewOrderResponse | null {
    return this.reviewOrderDataSubject.getValue();
  }

  getAccountList(
    cartId: string,
    accountType: AccountType,
    page = 0,
    searchTerm = '',
    searchBy = 'default'
  ): Observable<AccountListResponse> {
    const trimmedSearchTerm = searchTerm.trim();
    const trimmedSearchBy = searchBy.trim();
    const params: {
      pageSize: number;
      pageNumber: string;
      isTransferPopup: boolean;
      accountType: AccountType;
      tabType: string;
      popupType: string;
      showMoreCounter: string;
      searchTerm?: string;
      searchBy?: string;
    } = {
        "pageSize": 10,
        "pageNumber": page.toString(),
        "isTransferPopup": false,
        "accountType": accountType,
        "tabType": "RECENT",
        "popupType": "CHANGE_ACCOUNT",
        "showMoreCounter":"1"
    };
    if (trimmedSearchTerm) {
      params.searchTerm = trimmedSearchTerm;
    }
    if (trimmedSearchBy && trimmedSearchBy !== 'default') {
      params.searchBy = trimmedSearchBy;
    }
    const url = this.occEndpointsService.buildUrl('agent/getB2bAccounts');
    return this.http.post<AccountListResponse | AccountUpdateResponse>(url, params).pipe(
      map(response => ({
        ...response,
        accountsType: response.accountsType ?? accountType,
        salesOrg: response.salesOrg ?? '',
      } as AccountListResponse)),
      catchError(error => {
        console.error('Failed to fetch account list:', error);
        return of(null as unknown as AccountListResponse);
      })
    );
  }

  updateAccount( accountUid: string, accountName: string, accountType: AccountType): Observable<AccountUpdateResponse> {
    const url = this.occEndpointsService.buildUrl(`/agent/changeShipToForCurrentCustomer`);
    const payload = {
          "accountUid": accountUid,
          "accountName": accountName,
          "accountType": accountType,
          "popupType": "CHANGE_ACCOUNT",
          "pageSize": 10,
          "pageNumber": 0,
          "customerUid": "",
        };
    return this.http.post<AccountUpdateResponse>(url, payload).pipe(
      catchError(error => {
        console.error('Failed to update account:', error);
        return of(null as unknown as AccountUpdateResponse);
      })
    );
  }

  /** Searches/paginates the Sold-To list via changeShipToForCurrentCustomer (Sold-To has no standalone list endpoint). */
  searchSoldToAccounts(
    accountUid: string,
    accountName: string,
    page = 0,
    searchTerm = '',
    searchBy = 'default'
  ): Observable<AccountListResponse> {
    const trimmedSearchTerm = searchTerm.trim();
    const trimmedSearchBy = searchBy.trim();
    const url = this.occEndpointsService.buildUrl(`/agent/changeShipToForCurrentCustomer`);
    const payload: {
      accountUid: string;
      accountName: string;
      accountType: AccountType;
      popupType: string;
      pageSize: number;
      pageNumber: number;
      customerUid: string;
      searchTerm?: string;
      searchBy?: string;
    } = {
          "accountUid": accountUid,
          "accountName": accountName,
          "accountType": 'SOLD_TO',
          "popupType": "CHANGE_ACCOUNT",
          "pageSize": 10,
          "pageNumber": page,
          "customerUid": "",
        };
    if (trimmedSearchTerm) {
      payload.searchTerm = trimmedSearchTerm;
    }
    if (trimmedSearchBy && trimmedSearchBy !== 'default') {
      payload.searchBy = trimmedSearchBy;
    }
    return this.http.post<AccountUpdateResponse>(url, payload).pipe(
      map(response => ({
        ...response,
        accountsType: response.accountsType ?? 'SOLD_TO',
        salesOrg: response.salesOrg ?? '',
      } as AccountListResponse)),
      catchError(error => {
        console.error('Failed to search sold-to accounts:', error);
        return of(null as unknown as AccountListResponse);
      })
    );
  }

  updateCartStatus(cartId: string, inReview: boolean): Observable<any> {
    // Last line of defence: a finalized business action owns the backend status.
    if (!inReview && this.isReviewFinalActionCart(cartId)) {
      return of(null);
    }

    const url = this.occEndpointsService.buildUrl('agent/updateCartStatus');
    return this.http.post<any>(url, { cartId, inReview }).pipe(
      catchError(error => {
        console.error('Failed to update cart status:', error);
        return of(null);
      })
    );
  }

  /** Returns the resolved URL string for use with sendBeacon (bypasses HttpClient). */
  buildUpdateCartStatusUrl(): string {
    return this.occEndpointsService.buildUrl('agent/updateCartStatus');
  }

  private sendUpdateCartStatusBeacon(cartId: string): void {
    if (this.isReviewFinalActionCart(cartId)) {
      return;
    }

    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      this.updateCartStatus(cartId, false).subscribe();
      return;
    }

    const statusUrl = this.buildUpdateCartStatusUrl();
    const statusPayload = JSON.stringify({ cartId, inReview: false });
    navigator.sendBeacon(statusUrl, new Blob([statusPayload], { type: 'application/json' }));
  }

  /** Persists current order details (header fields + line items) to the backend. */
  saveOrderDetails(payload: any): Observable<any> {
    const url = this.occEndpointsService.buildUrl(`agent/updateCartHeader`);
    return this.http.post<any>(url, payload);
  }

  /** Returns the resolved URL for saveOrderDetails for use with sendBeacon. */
  buildSaveOrderDetailsUrl(cartId: string): string {
    return this.occEndpointsService.buildUrl(`agent/updateCartHeader`);
  }

  /** Sends one dirty/deleted cart entry row to the backend. */
  updateCartEntries(payload: CartEntryUpdatePayload): Observable<any> {
    const url = this.occEndpointsService.buildUrl('agent/updateCartEntry');
    return this.http.post<any>(url, payload).pipe(
      catchError(error => {
        console.error('Failed to update cart entries:', error);
        // Rethrown so callers can block a user action when the save did not succeed.
        throw error;
      })
    );
  }

  /** Returns the resolved URL for updateCartEntries for use with sendBeacon. */
  buildUpdateCartEntryUrl(): string {
    return this.occEndpointsService.buildUrl('agent/updateCartEntry');
  }

  /** Submits the final order to the backend. */
  placeOrder(cartId: string): Observable<any> {
    const url = this.occEndpointsService.buildUrl(`agentcarts/${cartId}/agentplaceorder`);
    return this.http.post<any>(url,{cartId: cartId}).pipe(
      catchError(error => {
        console.error('Failed to place order:', error);
        throw error;
      })
    );
  }
  /** Sends the order to SN for consignment review. */
  consignmentReview(cartId: string): Observable<ConsignmentReviewRow[]> {
    const url = this.occEndpointsService.buildUrl(`agentcarts/serialdetails/${cartId}`);
    return this.http.get<ConsignmentReviewRow[]>(url).pipe(
      catchError(error => {
        console.error('Failed to send to SN:', error);
        throw error;
      })
    );
  }

  serialNumberValidation(cartId: string): Observable<SerialValidationEntry[] | null> {
    const url = this.occEndpointsService.buildUrl(`agentcarts/serialdetails/${cartId}`);
    return this.http.post<SerialValidationEntry[]>(url, {cartId}).pipe(
      catchError(error => {
        console.error('Failed to validate serial number:', error);
        return of(null);
      })
    );
  }

  /** Submits the reject-document form and puts the cart/document into rejection status. */
  rejectDocument(payload: RejectDocumentPayload): Observable<any> {
    const url = this.occEndpointsService.buildUrl(`agentcarts/serialdetails/sendRejectEmail/${payload.cartId}`);
    return this.http.post<any>(url, payload).pipe(
      catchError(error => {
        console.error('Failed to reject document:', error);
        throw error;
      })
    );
  }

  /** Loads reject reasons/emails for a salesOrg. */
  getRejectModalConfig(salesOrg: string): Observable<RejectModalConfig> {
    const url = this.occEndpointsService.buildUrl('components/agent-site-configuration');
    const params = new HttpParams().set('salesOrg', salesOrg);
    const splitValues = (value?: string) =>
      (value ?? '').split(',').map((item) => item.trim()).filter((item) => item.length > 0);

    return this.http.get<AgentSiteConfigurationResponse>(url, { params }).pipe(
      map((response) => {
        const entries = response?.agentSiteConsigurationDataList ?? [];
        const valueFor = (key: string) => entries.find((entry) => entry?.key === key)?.value;
        return {
          reasons: splitValues(valueFor('agent.reject.reason')),
          emails: splitValues(valueFor('agent.reject.emails')),
        };
      }),
      catchError((error) => {
        console.error('Failed to fetch reject modal configuration:', error);
        throw error;
      })
    );
  }

  /** Submits the consignment review modal data (mismatched ship-to rows, comment, notify emails). */
  saveConsignmentReview(payload: ConsignmentReviewPayload): Observable<any> {
    const url = this.occEndpointsService.buildUrl(`agentcarts/serialdetails/sendConsingmentEmail/${payload.cartId}`);
    return this.http.post<any>(url, payload).pipe(
      catchError(error => {
        console.error('Failed to save consignment review:', error);
        throw error;
      })
    );
  }

  getHomeComponentData(): Observable<TableDataResponse> {
    return this.homeComponentData$;
  }

  refreshHomeComponentData(): Observable<TableDataResponse> {
    this.homeComponentData$ = this.buildHomeComponentDataStream();
    return this.homeComponentData$;
  }

  getPurchaseOrders(tileTitle?: string): Observable<TableDataResponse> {
    // Purchase-order page now always loads the complete dataset,
    // and applies tile-specific status filtering on the client.
    return this.getDashboardDataAfterReviewExit(false);
  }

  private isLoginPage(): boolean {
    const path = window.location.pathname.toLowerCase();
    return path.includes('/login');
  }

  private initializeIdleLogout(): void {
    if (this.idleLogoutInitialized) {
      return;
    }

    this.auth.isUserLoggedIn().pipe(
      filter((isLoggedIn) => isLoggedIn),
      take(1),
      switchMap(() => this.homeComponentData$),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data: any) => {
        // Extract idle timeout from cached API response
        const idleTimeout = data?.idleUserLogoutTime;
        if (idleTimeout && typeof idleTimeout === 'number' && idleTimeout > 0) {
          // Update the subject so login page and other components can access the actual value
          this.currentIdleTimeoutSubject.next(idleTimeout);
          this.idleUserLogout(idleTimeout);
          this.idleLogoutInitialized = true;
        } else {
          // Fallback to environment config if API doesn't provide value
          this.currentIdleTimeoutSubject.next(environment.IdleUserLogoutTime);
          this.idleUserLogout(environment.IdleUserLogoutTime);
          this.idleLogoutInitialized = true;
        }
      },
      error: (error) => {
        console.error('Failed to initialize idle logout:', error);
        // Fallback to environment config on error
        this.currentIdleTimeoutSubject.next(environment.IdleUserLogoutTime);
        this.idleUserLogout(environment.IdleUserLogoutTime);
        this.idleLogoutInitialized = true;
      }
    });
  }

  idleUserLogout(idleUserLogoutTime: number) {
    this.bnIdle
      .startWatching(idleUserLogoutTime)
      .pipe(takeUntilDestroyed(this.destroyRef)) 
      .subscribe((isTimedOut: boolean) => {
        if (!isTimedOut) return;
        if (this.isLoginPage()) return;
        this.bnIdle.stopTimer();
        this.bnIdle.resetTimer();
        this.isLoggedOut = true;
        this.isSessionExpiredSubject.next(true);
        this.markActiveReviewCartLeft();
        this.auth.logout();
        this.authRedirectService.setRedirectUrl('/');
        this.authStorageService.removeItem('access_token');
        localStorage.removeItem('userId');
      });
  }
}
